import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
// growth-ds Kova CSS: modern-normalize reset + Kova tokens. Keep it the first import, before any module
// that brings CSS — each DS component imports its own styles. See docs/decisions/0020-growth-ui-kova.md.
import '@simple-life-app/growth-ui-kova/style.css'
import App from './App.vue'
import { router } from './router'
import { installCookiebotLoader, setupCookieConsentSync } from '@/cookiebot'
import { setupSentryConsentGate } from '@/sentry/init'
import { useAuthStore } from '@/store/auth'
import { useSubscriptionStore } from '@/store/subscription'
import { authLogin } from '@/api/medUsers'
import { useLocationStore } from '@/store/location'
import { useGeoStore } from '@/store/geo'
import analytics, { setAnalyticsUser } from '@/analytics/client'
import { trackCancellationSessionStart } from '@/analytics/events'

// Paths where the cancellation funnel actually starts: `/` (the dispatcher — the app's
// manage-subscription entry, CRM links) and `/status` (a live CRM/email arrival shape, verified
// e2e — see analytics.md). A raw ?from on any other arrival — /payment/update?from=app
// (DEV-35299), future deeplinks — must not log a funnel entry.
const CANCELLATION_FUNNEL_ENTRY_PATHS = ['/', '/status']

// '/status/' → '/status'; bare '/' stays '/'.
function normalizePath(pathname: string): string {
	return pathname.replace(/\/+$/, '') || '/'
}

async function bootstrap() {
	const app = createApp(App)
	const pinia = createPinia()
	pinia.use(piniaPluginPersistedstate)
	app.use(pinia)
	// Establish the analytics sessionId from the arrival URL before the router runs its first
	// navigation (the `/` dispatcher would otherwise strip ?from / ?cancellation_session_id).
	const location = useLocationStore()
	location.init()

	// Arm Sentry/GlitchTip error monitoring: wire Cookiebot consent → the `cookieConsent` store,
	// then subscribe the gate. Sentry.init() runs only once `statistics` consent is active, so
	// there is no Sentry network traffic before then. Done here (before mount, before the router
	// injects the Cookiebot script) so the first consent response is caught. See
	// docs/architecture/sentry.md.
	setupCookieConsentSync()
	setupSentryConsentGate(app)

	// Seed the analytics context that needs Pinia (geo GDPR flag + cancellation session id), then
	// emit CancellationSessionStart at the cancellation-funnel entry — the pre-Auth0 "redirect
	// screen" from the GLP Cancellation Funnel Logging spec. `arrivalFrom` is set only when THIS
	// load carries a raw `?from`, so the event fires once per entry (landing | telegra | app |
	// email) and never on reloads or the Auth0 return. The entry-path allowlist keeps non-funnel
	// deep links that also carry ?from=app (e.g. /payment/update, DEV-35299) out of the funnel
	// metrics. For `from=app` the app supplies the cancellation_session_id via the URL; we still
	// emit the web-side event.
	analytics.setContext({
		application: { app_gdpr_enabled: useGeoStore().isEU },
		cancellation: { cancellation_session_id: location.sessionId },
	})
	if (location.arrivalFrom && CANCELLATION_FUNNEL_ENTRY_PATHS.includes(normalizePath(location.arrivalPathname))) {
		trackCancellationSessionStart(location.sessionId)
	}

	app.use(router)
	installCookiebotLoader(router)

	const auth = useAuthStore()
	await auth.init()

	// Subscription must be available before the first render of any non-/signin route.
	// We block here only when authenticated; unauthenticated users go straight to /signin
	// and there is nothing to fetch. The store swallows fetch errors into `error` so a
	// flaky backend does not prevent the app from mounting.
	if (auth.isAuthenticated) {
		// The billing customer_id is derived from the Auth0 token server-side, so the fetch
		// takes no argument. We still read user.sub to stamp the analytics user.
		const customerId = readCustomerId(auth.user)
		if (customerId) setAnalyticsUser(customerId)
		// Finalize the med-users session first: med-billing-engine derives the billing
		// customer_id from it, so the subscription endpoint 401s ("authenticated user has no
		// signup session") until this runs. Non-fatal on failure — the subscription load
		// below surfaces the resulting error on /status.
		try {
			auth.setProfile(await authLogin())
		} catch (err) {
			console.error('med-users auth/login failed:', err)
		}
		await useSubscriptionStore().fetchSubscription()
	}

	app.mount('#app')
}

// Auth0 user.sub is the canonical identifier we send to the subscription backend.
// Shape from @auth0/auth0-spa-js: { sub: string, email?: string, ... }.
function readCustomerId(user: unknown): string | null {
	if (user && typeof user === 'object' && 'sub' in user) {
		const sub = (user as { sub: unknown }).sub
		return typeof sub === 'string' ? sub : null
	}
	return null
}

bootstrap()
